import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Database, ArrowUpRight, Dna, Brain, Activity, Syringe, GitBranch, BarChart3 } from 'lucide-react';

const SECTIONS = [
  {
    heading: 'Data raredisease',
    blurb: 'Run queries and shape data without standing up infrastructure.',
    items: [
      {
        path: '/tools/csv_query',
        icon: Database,
        label: 'CSV Query',
        description: 'Upload CSVs and run SQL against them instantly. Visualize results as charts — all in the browser.',
        tags: ['DuckDB', 'SQL', 'Recharts'],
        accent: '#5bb8f5',
        meta: 'Tool',
      },
    ],
  },
  {
    heading: 'Rare Disease Stories',
    blurb: 'Interactive stories about life with a rare diagnosis.',
    items: [
      {
        path: '/raredisease/overview',
        icon: BarChart3,
        label: 'Rare Diseases',
        description: '300 million people. Visualization of rare disease magnitude, pathways, and treatments.',
        tags: ['Rare Disease', 'Data Visualization'],
        accent: '#00e5b0',
        meta: '4 acts',
        featured: true,
      },
      // {
      //   path: '/raredisease/epilepsy',
      //   icon: Activity,
      //   label: 'Epilepsy Journey',
      //   description: 'Follow a baby through seizures, diagnostic delays, and insurance battles — the hidden cost of time.',
      //   tags: ['Scrollytelling', 'Timeline'],
      //   accent: '#f5a623',
      //   meta: '4 acts',
      // },
      // {
      //   path: '/raredisease/zolgensma',
      //   icon: GitBranch,
      //   label: 'Zolgensma Choice',
      //   description: 'An interactive choose-your-path SMA story. Every day lost is neurons lost forever.',
      //   tags: ['Branching', 'Interactive'],
      //   accent: '#e55b8a',
      //   meta: 'Interactive',
      // },
      // {
      //   path: '/raredisease/pku',
      //   icon: Dna,
      //   label: 'PKU Pathway',
      //   description: 'Scroll through phenylalanine metabolism — from dietary protein to the PAH enzyme bottleneck.',
      //   tags: ['GSAP', 'SmilesDrawer', 'Biochem'],
      //   accent: '#66d9c2',
      //   meta: '5 steps',
      // },
      {
        path: '/raredisease/pde',
        icon: Brain,
        label: 'PDE Pathway',
        description: 'Pyridoxine-dependent epilepsy — how a cofactor heist disrupts B6 metabolism and triggers seizures.',
        tags: ['B6', 'epilepsy', 'PDE'],
        accent: '#a78bfa',
        meta: 'Pathway',
      },
      // {
      //   path: '/raredisease/rare_disease',
      //   icon: Syringe,
      //   label: 'Rare Disease Overview',
      //   description: 'A broader look at the rare disease landscape — pathways, treatments, and the road to diagnosis.',
      //   tags: ['GSAP', 'ScrollTrigger'],
      //   accent: '#f472b6',
      //   meta: 'Overview',
      // },
    ],
  },
];

function hexToRgba(hex, alpha) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function ToolCard({ tool, navigate }) {
  const Icon = tool.icon;
  const featured = tool.featured;

  return (
    <button
      onClick={() => navigate(tool.path)}
      style={{
        position: 'relative',
        display: 'flex', flexDirection: 'column',
        gap: 14,
        padding: featured ? '24px' : '20px',
        background: 'var(--bg2)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        cursor: 'pointer', textAlign: 'left', width: '100%',
        color: 'var(--text)',
        gridColumn: featured ? '1 / -1' : 'auto',
        overflow: 'hidden',
        transition: 'transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = 'translateY(-3px)';
        e.currentTarget.style.borderColor = hexToRgba(tool.accent, 0.55);
        e.currentTarget.style.boxShadow = `0 12px 32px -12px ${hexToRgba(tool.accent, 0.45)}`;
        const arrow = e.currentTarget.querySelector('[data-arrow]');
        if (arrow) { arrow.style.transform = 'translate(2px, -2px)'; arrow.style.opacity = '1'; }
        const glow = e.currentTarget.querySelector('[data-glow]');
        if (glow) glow.style.opacity = '1';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.borderColor = 'var(--border)';
        e.currentTarget.style.boxShadow = 'none';
        const arrow = e.currentTarget.querySelector('[data-arrow]');
        if (arrow) { arrow.style.transform = 'translate(0, 0)'; arrow.style.opacity = '0.5'; }
        const glow = e.currentTarget.querySelector('[data-glow]');
        if (glow) glow.style.opacity = '0';
      }}
    >
      {/* Accent glow blob */}
      <div
        data-glow
        style={{
          position: 'absolute', top: -60, right: -60,
          width: 160, height: 160, borderRadius: '50%',
          background: `radial-gradient(circle, ${hexToRgba(tool.accent, 0.28)}, transparent 70%)`,
          opacity: 0, transition: 'opacity 0.25s ease',
          pointerEvents: 'none',
        }}
      />

      {/* Top accent bar */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: `linear-gradient(90deg, ${tool.accent}, ${hexToRgba(tool.accent, 0)})`,
      }} />

      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
        <div style={{
          width: 44, height: 44, borderRadius: 10, flexShrink: 0,
          background: hexToRgba(tool.accent, 0.12),
          border: `1px solid ${hexToRgba(tool.accent, 0.3)}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon size={20} style={{ color: tool.accent }} />
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3,
          }}>
            <span style={{
              fontWeight: 600,
              fontSize: featured ? 18 : 15,
              letterSpacing: '-0.01em',
            }}>
              {tool.label}
            </span>
            {/* <span style={{
              fontSize: 10, fontFamily: 'var(--mono)',
              color: tool.accent,
              background: hexToRgba(tool.accent, 0.1),
              border: `1px solid ${hexToRgba(tool.accent, 0.25)}`,
              padding: '2px 7px', borderRadius: 999,
              textTransform: 'uppercase', letterSpacing: '0.05em',
            }}>
              {tool.meta}
            </span> */}
          </div>
          <div style={{
            color: 'var(--text2)', fontSize: 13, lineHeight: 1.55,
          }}>
            {tool.description}
          </div>
        </div>

        <ArrowUpRight
          data-arrow
          size={18}
          style={{
            color: tool.accent, flexShrink: 0, opacity: 0.5,
            transition: 'transform 0.18s ease, opacity 0.18s ease',
          }}
        />
      </div>

      {/* Tag chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {tool.tags.map(t => (
          <span key={t} style={{
            fontSize: 10.5, fontFamily: 'var(--mono)',
            color: 'var(--text3)',
            background: 'var(--bg3)',
            border: '1px solid var(--border)',
            padding: '3px 8px', borderRadius: 5,
          }}>
            {t}
          </span>
        ))}
      </div>
    </button>
  );
}

export default function Home2() {
  const navigate = useNavigate();

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--bg)',
      display: 'flex', flexDirection: 'column',
      fontFamily: 'var(--sans)',
      overflowY: 'auto',
    }}>
      {/* Hero */}
      <div style={{
        textAlign: 'center',
        padding: '80px 32px 56px',
      }}>
        <h1 style={{
          fontSize: 'clamp(40px, 8vw, 72px)',
          fontWeight: 700,
          letterSpacing: '-0.02em',
          color: 'var(--text)',
          marginBottom: 20,
          lineHeight: 1,
        }}>
          maca<span style={{ color: 'var(--orange)' }}>row</span>ni
        </h1>

        <p style={{
          color: 'var(--text2)', fontSize: 15, maxWidth: 540, margin: '0 auto',
          lineHeight: 1.6,
        }}>
          Interactive data raredisease and scrollytelling stories.{' '}
          <span style={{ color: 'var(--text)', fontWeight: 500 }}>Built for clarity.</span>{' '}
          <span style={{ color: 'var(--orange)', fontWeight: 500 }}>No data leaves your browser.</span>
        </p>
      </div>

      {/* Sections */}
      <div style={{
        padding: '0 32px 80px',
        maxWidth: 760, margin: '0 auto', width: '100%',
        display: 'flex', flexDirection: 'column', gap: 48,
      }}>
        {SECTIONS.map(section => (
          <div key={section.heading}>
            <div style={{ marginBottom: 16 }}>
              <div style={{
                fontSize: 11, color: 'var(--text3)',
                textTransform: 'uppercase', letterSpacing: '0.08em',
                marginBottom: 4,
              }}>
                {section.heading}
              </div>
              <div style={{ color: 'var(--text2)', fontSize: 13 }}>
                {section.blurb}
              </div>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(330px, 1fr))',
              gap: 12,
            }}>
              {section.items.map(tool => (
                <ToolCard key={tool.path} tool={tool} navigate={navigate} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
